import {ServiceBase} from "../ServiceBase";
import {GraphyteArea, GraphyteLayoutApiResponse, IGraphyteService} from "./graphyte.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {action, observable} from "mobx";
import {Check} from "@sparkware/app-utils";
import {SwiperGamesListConfiguration} from "../configurations/config-data-sections/arenas.configuration";

const eventCategory = "GRAPHYTE";
const eventAction = "FETCH_RECOMMENDATIONS";

export class GraphyteService extends ServiceBase implements IGraphyteService {
    constructor(services: IServiceFactoryExtended){
        super(services);

        this._injectPreconnectURL();

        this.reaction(() => this.services.user.isAuthorized, (isAuthorized: boolean) => {
            if(isAuthorized &&
                this.services.configuration.data.homePage.graphyte?.enabled &&
                this.services.abTestingFeatureDataProvider.areGraphyteListsEnabled
               )
            {
               this._fetchGraphyteRecommendations();
            }
        }, {
            fireImmediately: true
        });
    }

    @observable.ref homepageLists: SwiperGamesListConfiguration[] = null;

    @action.bound
    private _setHomepageLists(lists: SwiperGamesListConfiguration[]): void {
        this.homepageLists = lists;
    }

    private _injectPreconnectURL() {

        try {

            const link = this.services.dom.createElement("link");

            link.setAttribute("rel", "preconnect");
            link.setAttribute("href", "https://api.graphyte.ai");

            this.services.dom.head.appendChild(link);

        } catch (error) {
            this.services.logger.error(`Something went wrong with the preconnect to the Graphyte domain.`)
        }
    }

    private _fetchGraphyteRecommendations(): void {
        const config = this.services.configuration.servicesConfigs.Graphyte;
        const homepageLayoutId = this.services.configuration.data.homePage.graphyte?.layoutId;
        const requestUrl = config.layoutApiUrl.replace("{layoutId}", homepageLayoutId);

        const options: RequestInit = {
          headers: this._buildRequestHeaders(config.apiKey, config.brandKey),
          body: this._buildRequestBody()
        };

        this.services.httpWithRequestTimeMeasurement.post(requestUrl, options).then((response: GraphyteLayoutApiResponse) => {
            this._setHomepageLists(this._parseGraphyteLists(response));
        }).catch(err => {
         this._setHomepageLists(null);
         this._logError(err);
        });
    }

    private _buildRequestHeaders(apiKey: string, brandKey: string): HeadersInit {
        let headers = this.services.httpWithRequestTimeMeasurement.createHeaders();
        headers.append('Content-Type', 'application/json');
        headers.append('x-api-key', apiKey);
        headers.append('x-brand-key', brandKey);
        return headers;
    }

    private _buildRequestBody(): string {
        const userId = this.services.user.cid;
        const channel = this.services.device.os.isMobileOS ? "Mobile" : "Desktop";
        const bodyObject = {
          "userId": userId,
          "type": "layout",
          "context": {
              "channel": channel
          }
        };
        return JSON.stringify(bodyObject);
    }

    private _parseGraphyteLists(response: GraphyteLayoutApiResponse): SwiperGamesListConfiguration[] {
        const result : SwiperGamesListConfiguration[] = [];

        response.layout.result.forEach(areaWrapper => {
            const parsedArea: SwiperGamesListConfiguration = this._parseGraphyteArea(areaWrapper.area);
            if(parsedArea.gamesId.length === 0){
                return;
            }
            result.push(parsedArea);
        });

        if(result.length === 0){
            throw new Error("no Graphyte lists, defaulting back to 888 strategy");
        }

        return result;
    }

    private _parseGraphyteArea(area: GraphyteArea): SwiperGamesListConfiguration {
        let gameTypes = area.placement.result.map(gameWrapper => parseInt(gameWrapper.game.game_code));

        let result =  {
            display: true,
            title: area.placement.title,
            gamesId: gameTypes,
            seeAll: '',
            templateType: '',
            listId: area.index
        };

        //Area.placement.extra is the equivalent of metadata from our admin
        if(!Check.isNullOrUndefined(area.placement.extra)){
            Object.keys(area.placement.extra).forEach(key => {
                result[key] = area.placement.extra[key];
            });
        }

        return result;
    }

    private _logError(error: any): void {
        try{
            error = error?.message || JSON.stringify(error).replace(/{|}/g, "=");
        }
        catch (e){
            error="";
        }
        this.services.sdk.structuredLoggingService
            .getLogger()
            .addCategory(eventCategory)
            .addAction(eventAction)
            .addLabel(error)
            .info();

        this.services.logger.error("Error while fetching Graphyte data: " + error);
    }
}
